import type { SlashCommand } from "@codenexus/react";

import { resolveTaxonomy, type TaxonomyResolution } from "@/help/resolver";
import { normalizePathWithSearch } from "../utils/appViewRoutes";

// Global Help concierge skill id (KAN-1358). Fired by the header Help button and
// also used by the router's help-intent fast path.
export const GLOBAL_HELP_SKILL = "global-product-studio-help";

// `/help` goes through the normal send path so its context includes the current
// module and taxonomy. It intentionally has no host-navigation side effects.
export const HELP_SLASH_COMMANDS: SlashCommand[] = [
    {
        name: "help",
        description: "Find your way around Product Studio",
        mode: "skill",
        execute: ctx => ctx.send("help")
    }
];

// Canonical Global Help module vocabulary. The standalone new-product screen
// remains currentPage `new-product`, but belongs to Product Hub for help routing.
export function deriveCurrentModule(currentView: string | undefined): string {
    switch (currentView) {
        case "projects":
        case "new-product":
            return "product-hub";
        case "features":
            return "feature-studio";
        case "editor":
            return "work-product-editor";
        case "integrations":
            return "integrations-console";
        case "uploads":
            return "file-management";
        default:
            return "any-workspace";
    }
}

/** Views whose FDE tools must retain the currently selected product. */
export function isProductScopedChatView(currentView: string): boolean {
    return currentView === "features" || currentView === "editor" || currentView === "mesh-strength";
}

/** File-addition requests are direct navigation intents. Route them before the
 * agent's tool-search turn so the source page cannot emit a dwell welcome while
 * the navigation tool is still being resolved. */
export function isDirectFileAdditionPrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase();
    return (
        /\b(add|upload|attach)\b[\s\S]*\b(new\s+)?(file|document|attachment)\b/.test(prompt) ||
        /\b(new\s+)?(file|document|attachment)\b[\s\S]*\b(to|into)\b[\s\S]*\b(this|the|my)\s+product\b/.test(prompt)
    );
}

export function isDirectFilesPagePrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase();
    return (
        isDirectFileAdditionPrompt(prompt) ||
        /\b(go|navigate|open|take|switch)\b[\s\S]*\b(files|file\s+management)\b(?:\s+page)?/.test(prompt)
    );
}

/** Recognizes the short creation commands shown to users for Context Mesh. */
export function isDirectContextMeshCreationPrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase().replace(/[*_`]/g, "").replace(/[.!?]+$/, "").trim();
    return /^(?:please\s+)?(?:create(?:\s+a)?\s+context\s+mesh|add\s+context|build\s+context)$/.test(prompt);
}

/** Recognizes a create request that already includes discovery values. Unlike
 * the short command, the agent must continue into read/propose/confirm after
 * the host opens the form. */
export function isDetailedContextMeshCreationPrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase().replace(/[*_`]/g, "");
    return /^(?:please\s+)?create(?:\s+a)?\s+context\s+mesh\s+(?:for|named|called|with)\b[\s\S]+/.test(prompt);
}

/** Recognizes requests that should return the user's Context Mesh collection in chat. */
export function isContextMeshListPrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase().replace(/[*_`]/g, "").replace(/[.!?]+$/, "").trim();
    return (
        /\b(list|show|view)\b[\s\S]*\b(?:available\s+|my\s+)?context\s+mesh(?:es)?(?:\s+items?)?\b/.test(prompt) ||
        /\bwhat\b[\s\S]*\bcontext\s+meshes\b[\s\S]*\b(available|have|exist)\b/.test(prompt) ||
        /^(?:what are\s+)?(?:the\s+)?available\s+context\s+meshes$/.test(prompt)
    );
}

/** Recognizes returning from Score Detail Report to its owning Context Mesh. */
export function isScoreDetailBackToContextMeshPrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase().replace(/[*_`]/g, "").replace(/[.!?]+$/, "").trim();
    return /^(?:please\s+)?(?:go\s+)?back\s+to\s+(?:the\s+)?context\s+mesh(?:\s+page)?$/.test(prompt);
}

/** Activates the local report-close control, preserving the owning mesh route. */
export function activateScoreDetailBackControl(root: ParentNode): boolean {
    const button = root.querySelector<HTMLElement>('[data-ui-bridge="btn.close-score-detail-report"]');
    if (!button) return false;
    button.click();
    return true;
}

/** Extracts the mesh name from explicit existing-Context-Mesh navigation requests. */
export function resolveDirectContextMeshOpenPrompt(message: string): string | null {
    if (isDirectContextMeshHubPrompt(message) || isScoreDetailBackToContextMeshPrompt(message)) return null;
    const prompt = message.trim().replace(/[*_`]/g, "").replace(/[.!?]+$/, "").trim();
    const normalizeExtractedName = (value: string | undefined): string | null => {
        const name = value?.trim().replace(/[’']s$/i, "").trim();
        return name || null;
    };
    const suffixForm = prompt.match(/^(?:please\s+)?(?:open|view|show|go\s+to|take\s+me\s+to)\s+(?:the\s+)?(.+?)\s+context\s+mesh(?:\s+analysis(?:\s+page)?)?$/i);
    if (suffixForm?.[1]?.trim()) return normalizeExtractedName(suffixForm[1]);
    const prefixForm = prompt.match(/^(?:please\s+)?(?:open|view|show|go\s+to|take\s+me\s+to)\s+(?:the\s+)?context\s+mesh(?:\s+named)?\s+(.+)$/i);
    return normalizeExtractedName(prefixForm?.[1]);
}

/** Recognizes explicit navigation to the standalone Context Mesh Hub. */
export function isDirectContextMeshHubPrompt(message: string): boolean {
    const prompt = message.trim().toLowerCase().replace(/[*_`]/g, "").replace(/[.!?]+$/, "").trim();
    return /^(?:please\s+)?(?:go\s+to|navigate\s+to|open|take\s+me\s+to)\s+(?:the\s+)?context\s+mesh\s+hub(?:\s+(?:page|console))?$/.test(prompt);
}

export type DirectHierarchySelection = { kind: "feature" | "story"; name: string };

/** Recognizes an explicit named selection on the Feature & Story picker. */
export function resolveDirectHierarchySelectionPrompt(message: string): DirectHierarchySelection | null {
    const cleaned = message.trim().replace(/[*_`]/g, "").replace(/[.!?]+$/, "").trim();
    const match = cleaned.match(/^(?:please\s+)?(?:select|choose)\s+(?:the\s+)?(feature|(?:user\s+)?story)\s+(.+)$/i);
    if (!match?.[2]?.trim()) return null;
    return {
        kind: match[1].toLowerCase() === "feature" ? "feature" : "story",
        name: match[2].trim()
    };
}

export type SddStageNavigationKey = "specify" | "plan" | "task_breakdown" | "implement" | "validate";

/** Recognizes explicit Feature Studio stage navigation before generic skill
 * routing can confuse labels such as "Define Tasks" with another page. */
export function resolveDirectSddStagePrompt(message: string): SddStageNavigationKey | null {
    const prompt = message.trim().toLowerCase().replace(/[.!?]+$/, "");
    const match = prompt.match(/^(?:please\s+)?(?:go|navigate|open|take me|switch)(?:\s+me)?\s+(?:to\s+)?(?:the\s+)?(specify|plan|define tasks|task breakdown|implement|validate)(?:\s+stage)?$/);
    if (!match) return null;
    const stage = match[1];
    if (stage === "define tasks" || stage === "task breakdown") return "task_breakdown";
    return stage as SddStageNavigationKey;
}

/** Deep-link navigation already has an active FDE turn explaining the result. */
export function shouldFireFeatureStudioWelcome(context: unknown): boolean {
    return !(
        typeof context === "object" &&
        context !== null &&
        "suppressPageWelcome" in context &&
        context.suppressPageWelcome === true
    );
}

// Static views have one canonical route. Feature Studio and the editor are
// deliberately absent because their product/work-product hierarchy lives in
// the supplied browser route.
const VIEW_ROUTES: Record<string, string> = {
    projects: "/products",
    "new-product": "/products/new",
    "refresh-product": "/products/refresh",
    uploads: "/files",
    integrations: "/integrations",
    profile: "/profile",
    admin: "/admin"
};

export function routeWithSearch(pathname: string, search: string): string {
    return normalizePathWithSearch(pathname, search);
}

export function deriveHostLocationContext(currentView: string, currentRoute: string | undefined) {
    return {
        currentPage: currentView,
        currentRoute: VIEW_ROUTES[currentView] ?? currentRoute,
        currentModule: deriveCurrentModule(currentView)
    };
}

export interface AmbientProductContext {
    projectId: number | null;
    projectName: string | null;
}

interface ProductIdentitySource {
    id?: unknown;
    project_id?: unknown;
    projectid?: unknown;
    name?: unknown;
    project_name?: unknown;
}

function positiveProjectId(...candidates: unknown[]): number | null {
    for (const candidate of candidates) {
        const value = typeof candidate === "number" ? candidate : Number(candidate);
        if (Number.isSafeInteger(value) && value > 0) return value;
    }
    return null;
}

/** Normalizes the two project shapes used by Product Hub/Redux. In particular,
 * Product Hub selections carry the canonical id in `id`, while older screens
 * expose `project_id`; treating the name alone as live context would otherwise
 * hide the persisted id and make Context Mesh appear unscoped. */
export function resolveActiveProductContext(
    currentProject: ProductIdentitySource | null | undefined,
    selectedProject: ProductIdentitySource | null | undefined
): AmbientProductContext | null {
    const projectId = positiveProjectId(
        currentProject?.project_id,
        currentProject?.projectid,
        currentProject?.id,
        selectedProject?.project_id,
        selectedProject?.projectid,
        selectedProject?.id
    );
    const rawName =
        currentProject?.project_name ??
        currentProject?.name ??
        selectedProject?.project_name ??
        selectedProject?.name;
    const projectName = typeof rawName === "string" && rawName.trim() ? rawName.trim() : null;
    return projectId !== null || projectName !== null ? { projectId, projectName } : null;
}

export interface AmbientHierarchyContext {
    hierarchyLevel: "product" | "feature" | "user_story";
    hierarchyName: string;
    projectId?: string | number | null;
    projectName?: string | null;
    featureId?: string | number | null;
    featureName?: string | null;
    userStoryId?: string | null;
    userStoryName?: string | null;
}

export type ScopedWorkProductActivationResult =
    | { status: "activated" }
    | { status: "not_found" }
    | { status: "disabled" };

export interface ScopedWorkProductElement {
    workProductId: string;
    workProductName: string;
    scope: {
        level: "PRODUCT" | "FEATURE" | "USERSTORY";
        featureId: number | null;
        userStoryId: string | null;
    };
}

/** Reads the generation identity attached to a visible SDD work-product row.
 * This lets chat submit through the same job API as a manual Build click and
 * return the real processor job id instead of treating a DOM click as success. */
export function resolveScopedWorkProductElement(
    elementId: string,
    root: ParentNode = document
): ScopedWorkProductElement | null {
    const target = Array.from(root.querySelectorAll<HTMLElement>("[data-ui-bridge]"))
        .find(element => element.getAttribute("data-ui-bridge") === elementId);
    if (!target) return null;
    const workProductId = target.dataset.workProductId;
    const workProductName = target.dataset.workProductName;
    const level = target.dataset.workProductLevel;
    if (!workProductId || !workProductName || !["PRODUCT", "FEATURE", "USERSTORY"].includes(level || "")) return null;
    const featureId = target.dataset.workProductFeatureId ? Number(target.dataset.workProductFeatureId) : null;
    return {
        workProductId,
        workProductName,
        scope: {
            level: level as ScopedWorkProductElement["scope"]["level"],
            featureId: Number.isFinite(featureId) ? featureId : null,
            userStoryId: target.dataset.workProductUserStoryId || null
        }
    };
}

/** Activates an exact work-product card discovered through list_ui_elements.
 * Comparing attributes directly avoids rebuilding or CSS-escaping scoped ids. */
export function activateScopedWorkProductElement(
    elementId: string,
    root: ParentNode = document
): ScopedWorkProductActivationResult {
    const target = Array.from(root.querySelectorAll<HTMLElement>("[data-ui-bridge]"))
        .find(element => element.getAttribute("data-ui-bridge") === elementId);
    if (!target) return { status: "not_found" };
    if (target.hasAttribute("disabled") || target.getAttribute("aria-disabled") === "true") {
        return { status: "disabled" };
    }
    target.click();
    return { status: "activated" };
}

/** Ambient per-message context payload. Product identity is pushed with every
 *  message so agent skills don't depend on remembering to call
 *  get_current_product; nulls are sent explicitly on non-product screens so a
 *  stale product from a prior view can never leak through. */
export function buildViewContextPayload(
    currentView: string,
    currentRoute: string | undefined,
    activeProduct: AmbientProductContext | null | undefined,
    editorFields: Record<string, unknown>,
    activeHierarchy?: AmbientHierarchyContext | null
) {
    return {
        ...deriveHostLocationContext(currentView, currentRoute),
        activeProductId: activeProduct?.projectId ?? null,
        activeProductName: activeProduct?.projectName ?? null,
        // Send an explicit null outside product-scoped views so a prior feature
        // or story can never leak into a later FDE turn.
        activeHierarchy: activeHierarchy ?? null,
        ...editorFields
    };
}

export interface ChatTaxonomyResolution {
    matches: Array<{
        id: string;
        ui: string;
        score: number;
        matched_alias?: string;
    }>;
    version?: string;
}

// Core transports matched aliases in snake_case. Keep that protocol adaptation
// at the host boundary instead of leaking it into the application resolver.
export function adaptTaxonomyForChat({ matches, version }: TaxonomyResolution): ChatTaxonomyResolution {
    return {
        matches: matches.map(match => ({
            id: match.id,
            ui: match.ui,
            score: match.score,
            matched_alias: match.matchedAlias
        })),
        version
    };
}

export function resolveTaxonomyForChat(message: string): ChatTaxonomyResolution {
    return adaptTaxonomyForChat(resolveTaxonomy(message));
}

export interface SectionNavigationTarget {
    sectionId: string;
    sectionTitle: string;
    index: number;
}

export type SectionNavigationPlan =
    | { type: "confirm"; target: SectionNavigationTarget }
    | { type: "switch"; target: SectionNavigationTarget; restoreEditorView: boolean };

/**
 * Decides what a chip/agent-driven "navigate to section" request
 * (`handleNavigateToSection`) should do, given whether the currently-mounted
 * editor has unresolved staged edits and whether the editor is even the
 * active view right now.
 *
 * - Pending edits on the currently-mounted editor always win: surface the
 *   existing discard-confirmation flow, regardless of view. (In practice
 *   `hasPendingEdits()` can only be true when the editor is mounted, i.e.
 *   `currentView === "editor"` — there's no mounted TipTap instance to hold
 *   staged marks otherwise — but the check doesn't assume that.)
 * - Otherwise, switch directly. `restoreEditorView` tells the caller whether
 *   it must also flip the host view back to the editor before applying the
 *   tab switch — required whenever the editor isn't the active view (Feature
 *   Studio, Uploads, etc.), since a chip's entire point is to work from
 *   there. On-editor clicks are unaffected: `restoreEditorView` is false and
 *   the caller's existing behavior (switch tab, nothing else) is preserved.
 */
export function planSectionNavigation(
    target: SectionNavigationTarget,
    { currentView, hasPendingEdits }: { currentView: string; hasPendingEdits: boolean }
): SectionNavigationPlan {
    if (hasPendingEdits) {
        return { type: "confirm", target };
    }
    return { type: "switch", target, restoreEditorView: currentView !== "editor" };
}
